import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/app/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar, Check, ExternalLink, Lock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const CalendarSync = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [iCloudConnected, setICloudConnected] = useState(false);
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState("google");
  
  // Check if user is Pro
  const isPro = user?.plan === 'pro' || user?.plan === 'family';
  
  useEffect(() => {
    const checkConnections = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch user's calendar connections from Supabase
        const { data, error } = await supabase
          .from('ft_calendar_connections')
          .select('*')
          .eq('user_id', user.id);
          
        if (error) {
          console.error('Error fetching calendar connections:', error);
          return;
        }
        
        // Check for Google and iCloud connections
        if (data && data.length > 0) {
          const googleConn = data.find(conn => conn.provider === 'google');
          const iCloudConn = data.find(conn => conn.provider === 'icloud');
          
          setGoogleConnected(!!googleConn);
          setICloudConnected(!!iCloudConn);
          
          // Set sync enabled based on user preferences
          if (googleConn || iCloudConn) {
            setSyncEnabled(googleConn?.sync_enabled || iCloudConn?.sync_enabled || true);
          }
        }
      } catch (err) {
        console.error('Error checking calendar connections:', err);
      } finally {
        setLoading(false);
      }
    };
    
    checkConnections();
  }, [user]);
  
  const handleConnectGoogle = () => {
    if (!isPro) {
      toast({
        title: "Pro Feature",
        description: "Calendar sync is available for Pro and Family plans only.",
        variant: "destructive"
      });
      return;
    }
    
    // In a real implementation, this would redirect to Google OAuth flow
    // For now, we'll simulate a successful connection
    setLoading(true);
    
    setTimeout(async () => {
      try {
        // Simulate saving the connection to Supabase
        const { error } = await supabase
          .from('ft_calendar_connections')
          .upsert({
            user_id: user?.id,
            provider: 'google',
            provider_user_id: 'google_user_123',
            access_token: 'simulated_token',
            refresh_token: 'simulated_refresh_token',
            sync_enabled: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
          
        if (error) throw error;
        
        setGoogleConnected(true);
        setSyncEnabled(true);
        
        toast({
          title: "Google Calendar Connected",
          description: "Your Google Calendar has been connected successfully.",
          variant: "default"
        });
      } catch (err) {
        console.error('Error connecting Google Calendar:', err);
        toast({
          title: "Connection Failed",
          description: "Failed to connect Google Calendar. Please try again.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    }, 1500);
  };
  
  const handleConnectICloud = () => {
    if (!isPro) {
      toast({
        title: "Pro Feature",
        description: "Calendar sync is available for Pro and Family plans only.",
        variant: "destructive"
      });
      return;
    }
    
    // In a real implementation, this would redirect to iCloud authentication
    // For now, we'll simulate a successful connection
    setLoading(true);
    
    setTimeout(async () => {
      try {
        // Simulate saving the connection to Supabase
        const { error } = await supabase
          .from('ft_calendar_connections')
          .upsert({
            user_id: user?.id,
            provider: 'icloud',
            provider_user_id: 'icloud_user_123',
            access_token: 'simulated_token',
            refresh_token: 'simulated_refresh_token',
            sync_enabled: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
          
        if (error) throw error;
        
        setICloudConnected(true);
        setSyncEnabled(true);
        
        toast({
          title: "iCloud Calendar Connected",
          description: "Your iCloud Calendar has been connected successfully.",
          variant: "default"
        });
      } catch (err) {
        console.error('Error connecting iCloud Calendar:', err);
        toast({
          title: "Connection Failed",
          description: "Failed to connect iCloud Calendar. Please try again.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    }, 1500);
  };
  
  const handleDisconnectGoogle = async () => {
    setLoading(true);
    
    try {
      // Delete the Google connection from Supabase
      const { error } = await supabase
        .from('ft_calendar_connections')
        .delete()
        .eq('user_id', user?.id)
        .eq('provider', 'google');
        
      if (error) throw error;
      
      setGoogleConnected(false);
      
      toast({
        title: "Google Calendar Disconnected",
        description: "Your Google Calendar has been disconnected.",
        variant: "default"
      });
    } catch (err) {
      console.error('Error disconnecting Google Calendar:', err);
      toast({
        title: "Disconnection Failed",
        description: "Failed to disconnect Google Calendar. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDisconnectICloud = async () => {
    setLoading(true);
    
    try {
      // Delete the iCloud connection from Supabase
      const { error } = await supabase
        .from('ft_calendar_connections')
        .delete()
        .eq('user_id', user?.id)
        .eq('provider', 'icloud');
        
      if (error) throw error;
      
      setICloudConnected(false);
      
      toast({
        title: "iCloud Calendar Disconnected",
        description: "Your iCloud Calendar has been disconnected.",
        variant: "default"
      });
    } catch (err) {
      console.error('Error disconnecting iCloud Calendar:', err);
      toast({
        title: "Disconnection Failed",
        description: "Failed to disconnect iCloud Calendar. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleSync = async (enabled: boolean) => {
    setSyncEnabled(enabled);
    
    try {
      // Update all connections for this user
      const { error } = await supabase
        .from('ft_calendar_connections')
        .update({ sync_enabled: enabled, updated_at: new Date().toISOString() })
        .eq('user_id', user?.id);
        
      if (error) throw error;
      
      toast({
        title: enabled ? "Sync Enabled" : "Sync Disabled",
        description: enabled 
          ? "Calendar sync is now enabled. Your events will be synced automatically." 
          : "Calendar sync is now disabled. Your events will not be synced.",
        variant: "default"
      });
    } catch (err) {
      console.error('Error updating sync settings:', err);
      toast({
        title: "Update Failed",
        description: "Failed to update sync settings. Please try again.",
        variant: "destructive"
      });
      // Revert the UI state
      setSyncEnabled(!enabled);
    }
  };
  
  if (!isPro) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center mb-6">
          <Button 
            variant="ghost"
            size="icon"
            className="mr-3"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-black">Calendar Sync</h1>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-black">
              <Lock className="mr-2 h-5 w-5" />
              Pro Feature
            </CardTitle>
            <CardDescription>
              Calendar sync is available for Pro and Family plans only.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center py-8">
            <Calendar className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="font-medium text-lg mb-2 text-black">Upgrade to Pro</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Sync your FyndTyme events with Google Calendar and iCloud Calendar.
              Never miss an event with two-way synchronization.
            </p>
            <Button onClick={() => navigate('/pricing')}>
              <span className="text-white">View Pricing</span>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <Button 
          variant="ghost"
          size="icon"
          className="mr-3"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold text-black">Calendar Sync</h1>
        <Badge className="ml-2 bg-green-100 text-green-800">PRO</Badge>
      </div>
      
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-black">Sync Settings</CardTitle>
          <CardDescription>
            Control how your calendars sync with FyndTyme
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="sync-toggle" className="text-base font-medium text-black">
                Enable Calendar Sync
              </Label>
              <p className="text-sm text-muted-foreground">
                When enabled, your events will sync between FyndTyme and your connected calendars
              </p>
            </div>
            <Switch
              id="sync-toggle"
              checked={syncEnabled}
              onCheckedChange={handleToggleSync}
              disabled={loading || (!googleConnected && !iCloudConnected)}
            />
          </div>
        </CardContent>
      </Card>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="google">Google Calendar</TabsTrigger>
          <TabsTrigger value="icloud">iCloud Calendar</TabsTrigger>
        </TabsList>
        
        <TabsContent value="google">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-black">Google Calendar</CardTitle>
                  <CardDescription>
                    Sync your events with Google Calendar
                  </CardDescription>
                </div>
                <img 
                  src="/images/google-calendar-icon.png" 
                  alt="Google Calendar" 
                  className="h-10 w-10"
                  onError={(e) => {
                    // Fallback if image doesn't exist
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </CardHeader>
            <CardContent>
              {googleConnected ? (
                <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-start">
                  <Check className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-green-800">Connected</h4>
                    <p className="text-green-700 text-sm mt-1">
                      Your Google Calendar is connected and {syncEnabled ? 'syncing' : 'sync is paused'}.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-muted-foreground mb-4">
                    Connect your Google Calendar to sync events between FyndTyme and Google.
                  </p>
                  <Button 
                    onClick={handleConnectGoogle} 
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {loading ? "Connecting..." : "Connect Google Calendar"}
                  </Button>
                </div>
              )}
            </CardContent>
            {googleConnected && (
              <CardFooter className="flex justify-between border-t pt-6">
                <div className="text-sm text-muted-foreground">
                  Last synced: {new Date().toLocaleString()}
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleDisconnectGoogle}
                  disabled={loading}
                >
                  Disconnect
                </Button>
              </CardFooter>
            )}
          </Card>
        </TabsContent>
        
        <TabsContent value="icloud">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-black">iCloud Calendar</CardTitle>
                  <CardDescription>
                    Sync your events with Apple iCloud Calendar
                  </CardDescription>
                </div>
                <img 
                  src="/images/icloud-calendar-icon.png" 
                  alt="iCloud Calendar" 
                  className="h-10 w-10"
                  onError={(e) => {
                    // Fallback if image doesn't exist
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </CardHeader>
            <CardContent>
              {iCloudConnected ? (
                <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-start">
                  <Check className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-green-800">Connected</h4>
                    <p className="text-green-700 text-sm mt-1">
                      Your iCloud Calendar is connected and {syncEnabled ? 'syncing' : 'sync is paused'}.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-muted-foreground mb-4">
                    Connect your iCloud Calendar to sync events between FyndTyme and Apple devices.
                  </p>
                  <Button 
                    onClick={handleConnectICloud} 
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {loading ? "Connecting..." : "Connect iCloud Calendar"}
                  </Button>
                </div>
              )}
            </CardContent>
            {iCloudConnected && (
              <CardFooter className="flex justify-between border-t pt-6">
                <div className="text-sm text-muted-foreground">
                  Last synced: {new Date().toLocaleString()}
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleDisconnectICloud}
                  disabled={loading}
                >
                  Disconnect
                </Button>
              </CardFooter>
            )}
          </Card>
        </TabsContent>
      </Tabs>
      
      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-black mb-2">How Calendar Sync Works</h3>
        <p className="text-muted-foreground mb-4">
          Calendar sync allows you to keep your FyndTyme events in sync with your Google and iCloud calendars.
          Events created in FyndTyme will appear in your connected calendars, and events from your connected
          calendars can be imported into FyndTyme.
        </p>
        <ul className="space-y-2 text-sm text-muted-foreground mb-4">
          <li className="flex items-start">
            <Check className="h-4 w-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
            <span>Two-way synchronization keeps all your calendars up to date</span>
          </li>
          <li className="flex items-start">
            <Check className="h-4 w-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
            <span>Changes made in any calendar are reflected everywhere</span>
          </li>
          <li className="flex items-start">
            <Check className="h-4 w-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
            <span>Control which calendars sync with FyndTyme</span>
          </li>
        </ul>
        <a 
          href="https://fyndtyme.com.au/help/calendar-sync" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 text-sm inline-flex items-center"
        >
          Learn more about calendar sync
          <ExternalLink className="h-3 w-3 ml-1" />
        </a>
      </div>
    </div>
  );
};

export default CalendarSync;
